import { useSuspenseQuery } from "@apollo/client";
import {
  AssetCard,
  CurrencyAmountUI,
  DateUI,
  Icon,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { RelatedLedgerTransactions } from "~/components/RelatedLedgerTransactions";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/deposits_/$depositId")({
  head: () => opsHead("Deposit"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { depositId } = useParams({ strict: false });
  if (!depositId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminDepositPage($depositId: ID!) {
        deposit: node(id: $depositId) {
          id
          ... on Deposit {
            id
            shortRef
            ledgerTransactions {
              id
              ...RelatedLedgerTransactions
            }
            date
            amount {
              amount
              currency
            }
            organisation {
              id
              name
              squareLogoUrl
            }
            paymentReference
            description
            destinationAsset {
              id
              ...AssetCardFields
            }
          }
        }
      }
    `),
    {
      variables: {
        depositId,
      },
    },
  );

  const deposit = data?.deposit;
  if (!deposit || deposit.__typename !== "Deposit") {
    throw new NotFoundError();
  }

  useOpsTitle(`Deposit #${deposit.shortRef}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Deposit #${deposit.shortRef}`}
        subtitle={<div className="font-mono">{deposit.id}</div>}
      />
      <Tabs defaultValue="details">
        <TabsList size="sm" className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div>
            {deposit.organisation && (
              <DetailRow
                label="Organisation"
                value={
                  <Link
                    to={`/organisations/${deposit.organisation.id}`}
                    className="flex flex-row items-center gap-2 text-primary-interactive hover:text-blue-800 hover:underline"
                  >
                    <Icon
                      size="8"
                      imageUrl={
                        deposit.organisation?.squareLogoUrl ?? undefined
                      }
                      fallbackIcon={<PiBuildingOffice />}
                    />
                    <span>{deposit.organisation.name}</span>
                  </Link>
                }
              />
            )}
            {deposit.amount && (
              <DetailRow
                label="Amount"
                value={
                  <CurrencyAmountUI
                    amount={deposit.amount?.amount}
                    currency={deposit.amount.currency}
                    size="lg"
                    showSuperscript={false}
                  />
                }
              />
            )}
            <DetailRow
              label="Date"
              value={<DateUI date={deposit.date} variant="DATE_TZ" />}
            />
            {deposit.destinationAsset && (
              <DetailRow
                label="To Asset"
                value={<AssetCard asset={deposit.destinationAsset} />}
              />
            )}
            <DetailRow
              label="Reference"
              value={
                <span className="bg-neutral-100 p-2 font-mono text-neutral-700">
                  {deposit.paymentReference}
                </span>
              }
            />
            <DetailRow label="Description" value={deposit.description} />
          </div>
        </TabsContent>
        <TabsContent value="transactions">
          <RelatedLedgerTransactions
            transactions={deposit.ledgerTransactions ?? []}
          />
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}
